import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getWalletDashboard } from '../../../../redux/buzzbridge/actions';
import { fundContestFromWallet, getContestFundingRequirements } from '../../../../redux/contest/actions';
import {
    hasActiveCampaignHostPlanSelector,
    campaignHostPricingSelector,
} from '../../../../redux/campaignHostSubscription/selectors';
import { fetchCampaignHostPricing } from '../../../../redux/campaignHostSubscription/actions';
import type { IContestFundingRequirements } from '../../../../redux/contest/types';
import type { ICampaignHostPackage } from '../../../../types/global/campaignHostSubscription.types';
import { ROUTES } from '../../../../types/global/routes.types';
import { useDispatchPromise } from '../../../../utils/AppUtils';
import type { ICampaignCategory } from '../../../../components/EsForm/CampaignCategorySelector';
import { BrandCampaignSubscriptionGuard } from '../../subscription/BrandCampaignSubscriptionGuard';
import { CampaignHostTierPickerModal } from '../../subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from '../../subscription/CampaignHostCheckoutDialog';
import { useCampaignHostCheckout } from '../../subscription/useCampaignHostCheckout';
import {
    formatUsdFromCents,
    getFundingAwardDisplayRows,
    getFundingFeeLineItems,
    getFundingGrossRequiredCents,
    getFundingListedAwardsTotalCents,
    getFundingLockCents,
    hasPlatformFeeBreakdown,
    parseInsufficientWallet,
} from '../fundingUtils';
import { editorialHeadlineSx, editorialLabelSx, midnight } from '../midnightStyles';

interface Props {
    contestId: string;
    contestTitle: string;
    /** Wizard had cash prizes configured — used to detect failed award provisioning. */
    expectCashPrizes?: boolean;
    /** Used to label tiered prizes when the funding API omits human-readable tier names. */
    wizardCategories?: ICampaignCategory[];
    onFundingStatusChange?: (canPublish: boolean) => void;
}

export function ContestFundingStep({
    contestId,
    contestTitle,
    expectCashPrizes = false,
    wizardCategories,
    onFundingStatusChange,
}: Props) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();

    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const hostPricing = useSelector(campaignHostPricingSelector);
    const perCampaignFeeCents = hostPricing?.per_campaign_fee_cents ?? 5000;

    const [requirements, setRequirements] = useState<IContestFundingRequirements | null>(null);
    const [loading, setLoading] = useState(true);
    const [funding, setFunding] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [subGuardOpen, setSubGuardOpen] = useState(false);
    const [tierPickerOpen, setTierPickerOpen] = useState(false);

    const handleFundFromWalletRef = useRef<() => Promise<void>>(async () => {});

    const loadRequirements = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            dispatch(getWalletDashboard());
            const data = (await dispatchPromise(
                getContestFundingRequirements(contestId),
            )) as IContestFundingRequirements;
            setRequirements(data);
        } catch {
            setError('Could not load funding requirements. Try again in a moment.');
        } finally {
            setLoading(false);
        }
    }, [contestId, dispatch, dispatchPromise]);

    useEffect(() => {
        void loadRequirements();
    }, [loadRequirements]);

    useEffect(() => {
        if (!onFundingStatusChange) return;
        const canPublish =
            Boolean(requirements?.is_fully_funded) ||
            (requirements != null && requirements.required_total_cents === 0 && !expectCashPrizes);
        onFundingStatusChange(canPublish);
    }, [requirements, onFundingStatusChange, expectCashPrizes]);

    const handleFundFromWallet = async () => {
        if (!hasPlan) {
            if (!hostPricing) dispatch(fetchCampaignHostPricing());
            setSubGuardOpen(true);
            return;
        }
        if (!requirements || getFundingLockCents(requirements) <= 0) return;
        if (requirements.wallet_shortfall_cents > 0) return;

        setFunding(true);
        setError(null);
        try {
            const result = (await dispatchPromise(fundContestFromWallet({ contestId }))) as {
                requirements?: IContestFundingRequirements;
            };
            if (result?.requirements) {
                setRequirements(result.requirements);
            } else {
                await loadRequirements();
            }
            dispatch(getWalletDashboard());
        } catch (err) {
            const ins = parseInsufficientWallet(err);
            if (ins) {
                setError(
                    `Your campaign wallet needs ${formatUsdFromCents(
                        ins.shortfall_cents,
                    )} more before you can lock these funds.`,
                );
                dispatch(getWalletDashboard());
                await loadRequirements();
            }
        } finally {
            setFunding(false);
        }
    };
    handleFundFromWalletRef.current = handleFundFromWallet;

    const { checkoutOpen, setCheckoutOpen, beginMonthly, beginLtd, checkoutTitle, onPaymentMethodReady } =
        useCampaignHostCheckout({
            onSubscriptionComplete: () => {
                setSubGuardOpen(false);
                void handleFundFromWalletRef.current();
            },
        });

    const handleSelectMonthly = (pkg: ICampaignHostPackage) => {
        setTierPickerOpen(false);
        beginMonthly(pkg);
    };
    const handleSelectLtd = (pkg: ICampaignHostPackage) => {
        setTierPickerOpen(false);
        beginLtd(pkg);
    };

    const fundedPct =
        requirements && requirements.required_total_cents > 0
            ? Math.min(100, Math.round((requirements.funded_cents / requirements.required_total_cents) * 100))
            : 100;

    const lockCents = requirements ? getFundingLockCents(requirements) : 0;
    const feeLineItems = requirements ? getFundingFeeLineItems(requirements) : null;
    const showFeeBreakdown = Boolean(requirements && hasPlatformFeeBreakdown(requirements));
    const awardRows = requirements ? getFundingAwardDisplayRows(requirements, wizardCategories) : [];
    const listedAwardsTotalCents = getFundingListedAwardsTotalCents(awardRows);
    const showTierFundingNote =
        Boolean(requirements?.mode === 'tier') && listedAwardsTotalCents > (requirements?.required_total_cents ?? 0);

    const needsWalletTopUp = Boolean(requirements && requirements.wallet_shortfall_cents > 0);
    const needsContestFunding = Boolean(requirements && lockCents > 0);
    const isFullyFunded = Boolean(requirements?.is_fully_funded);
    const missingAwards =
        expectCashPrizes && requirements != null && (requirements.required_total_cents ?? 0) === 0 && !isFullyFunded;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: midnight.primary }} />
            </Box>
        );
    }

    if (error && !requirements) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
                <Button onClick={() => void loadRequirements()} sx={{ color: midnight.primary, fontWeight: 700 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                    Fund Prize Pool
                </Typography>
                <Typography sx={{ color: midnight.onSurfaceVariant, mt: 1, fontSize: 14 }}>
                    Lock prize money from your campaign wallet into this contest&apos;s escrow. You can publish once the
                    pool is fully funded.
                </Typography>
            </Box>

            <Box
                sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: `1px solid ${midnight.outline}`,
                    bgcolor: midnight.surfaceContainer,
                }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
                    }}>
                    {/* Summary panel */}
                    <Box
                        sx={{
                            p: { xs: 3, md: 4 },
                            background: `linear-gradient(135deg, ${midnight.surfaceContainer} 0%, ${midnight.surface} 100%)`,
                            borderRight: { md: `1px solid rgba(255,255,255,0.06)` },
                            borderBottom: { xs: `1px solid rgba(255,255,255,0.06)`, md: 'none' },
                        }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'rgba(255,201,101,0.1)',
                                color: midnight.primary,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                mb: 2,
                            }}>
                            <VerifiedOutlinedIcon sx={{ fontSize: 14 }} />
                            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em' }}>
                                PRIZE POOL
                            </Typography>
                        </Box>
                        <Typography sx={{ ...editorialHeadlineSx, fontSize: 26, color: midnight.onSurface, mb: 1 }}>
                            {requirements?.contest_title || contestTitle}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, mb: 3, lineHeight: 1.6 }}>
                            {requirements?.mode === 'tier'
                                ? 'For tiered contests, we fund the highest prize in each placement across all submission tiers.'
                                : 'Total required is the sum of all award prizes for this contest.'}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ bgcolor: midnight.surfaceLow, p: 2, borderRadius: '12px' }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 0.5 }}>Required prize pool</Typography>
                                <Typography sx={{ fontSize: 28, fontWeight: 800, color: midnight.primary }}>
                                    {formatUsdFromCents(requirements?.required_total_cents ?? 0)}
                                </Typography>
                                {showFeeBreakdown && requirements && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: midnight.onSurfaceVariant,
                                                mt: 1,
                                            }}>
                                            Total from wallet (incl. platform fees)
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 20,
                                                fontWeight: 700,
                                                color: midnight.onSurface,
                                                mt: 0.25,
                                            }}>
                                            {formatUsdFromCents(getFundingGrossRequiredCents(requirements))}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ bgcolor: midnight.surfaceLow, p: 2, borderRadius: '12px' }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 0.5 }}>Locked in escrow</Typography>
                                <Typography sx={{ fontSize: 28, fontWeight: 800, color: midnight.onSurface }}>
                                    {formatUsdFromCents(requirements?.funded_cents ?? 0)}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={fundedPct}
                                    sx={{
                                        mt: 2,
                                        height: 6,
                                        borderRadius: 999,
                                        bgcolor: midnight.surfaceHighest,
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: midnight.secondary,
                                            boxShadow: '0 0 8px rgba(0,253,193,0.4)',
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: 11, color: midnight.onSurfaceVariant, mt: 1 }}>
                                    {fundedPct}% funded
                                    {needsContestFunding && ` · ${formatUsdFromCents(lockCents)} due from wallet`}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Actions panel */}
                    <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: midnight.surfaceHighest }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 3, color: midnight.onSurface }}>
                            Campaign wallet
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                mb: 3,
                                borderRadius: '12px',
                                bgcolor: midnight.surfaceContainer,
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}>
                            <AccountBalanceWalletOutlinedIcon sx={{ color: midnight.secondary, fontSize: 28 }} />
                            <Box>
                                <Typography sx={{ ...editorialLabelSx }}>Available balance</Typography>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: midnight.onSurface }}>
                                    {formatUsdFromCents(requirements?.wallet_balance_cents ?? 0)}
                                </Typography>
                            </Box>
                        </Box>

                        {error && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {needsWalletTopUp && (
                            <Alert
                                severity="info"
                                sx={{ mb: 2, bgcolor: 'rgba(0,253,193,0.08)', color: midnight.onSurface }}>
                                Add <strong>{formatUsdFromCents(requirements?.wallet_shortfall_cents ?? 0)}</strong> to
                                your campaign wallet before you can lock prize funds. Top up on the Wallet page, then
                                return here.
                            </Alert>
                        )}

                        {isFullyFunded && !missingAwards && (
                            <Alert
                                severity="success"
                                sx={{ mb: 2, bgcolor: 'rgba(0,253,193,0.12)', color: midnight.onSurface }}>
                                Prize pool is fully funded. Use <strong>Publish campaign</strong> below to go live.
                            </Alert>
                        )}

                        {awardRows.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 1.5 }}>Awards</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    {awardRows.map(row => (
                                        <Box
                                            key={row.id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                py: 1.25,
                                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                            }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        color: midnight.onSurface,
                                                    }}>
                                                    {row.displayTitle}
                                                </Typography>
                                                {row.tierLabel && row.displayTitle !== row.placement && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 11,
                                                            color: midnight.onSurfaceVariant,
                                                            mt: 0.25,
                                                        }}>
                                                        Tier: {row.tierLabel}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: midnight.primary,
                                                    flexShrink: 0,
                                                }}>
                                                {formatUsdFromCents(row.prizeCents)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                {showTierFundingNote && (
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: midnight.onSurfaceVariant,
                                            mt: 1.5,
                                            lineHeight: 1.5,
                                        }}>
                                        Tiered contests lock the highest prize per placement (
                                        {formatUsdFromCents(requirements?.required_total_cents ?? 0)} total), not the
                                        sum of every tier line above.
                                    </Typography>
                                )}
                                {(requirements?.non_tiered_awards_cents ?? 0) > 0 && (
                                    <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mt: 1 }}>
                                        Plus {formatUsdFromCents(requirements!.non_tiered_awards_cents!)} in global
                                        awards
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {showFeeBreakdown && feeLineItems && (
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 2,
                                    borderRadius: '12px',
                                    bgcolor: midnight.surfaceContainer,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 1.5 }}>Wallet lock breakdown</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: midnight.onSurface }}>
                                            Prize pool
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: midnight.onSurface }}>
                                            {formatUsdFromCents(feeLineItems.prizePoolCents)}
                                        </Typography>
                                    </Box>
                                    {feeLineItems.flatFeeCents > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                            <Typography
                                                sx={{ fontSize: 13, fontWeight: 600, color: midnight.onSurface }}>
                                                Platform flat fee
                                            </Typography>
                                            <Typography
                                                sx={{ fontSize: 13, fontWeight: 700, color: midnight.onSurface }}>
                                                {formatUsdFromCents(feeLineItems.flatFeeCents)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {feeLineItems.percentageFeeCents > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                            <Typography
                                                sx={{ fontSize: 13, fontWeight: 600, color: midnight.onSurface }}>
                                                Platform fee ({feeLineItems.percentage}%)
                                            </Typography>
                                            <Typography
                                                sx={{ fontSize: 13, fontWeight: 700, color: midnight.onSurface }}>
                                                {formatUsdFromCents(feeLineItems.percentageFeeCents)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            pt: 1,
                                            borderTop: '1px solid rgba(255,255,255,0.08)',
                                        }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: midnight.onSurface }}>
                                            Total platform fees
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: midnight.primary }}>
                                            {formatUsdFromCents(feeLineItems.totalFeeCents)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            pt: 1,
                                            borderTop: '1px dashed rgba(255,255,255,0.12)',
                                        }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: midnight.onSurface }}>
                                            {needsContestFunding ? 'Due from wallet' : 'Total from wallet'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: midnight.secondary }}>
                                            {formatUsdFromCents(
                                                needsContestFunding
                                                    ? lockCents
                                                    : getFundingGrossRequiredCents(requirements!),
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                {requirements?.platform_fees?.applied_on && (
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            color: midnight.onSurfaceVariant,
                                            mt: 1.5,
                                            lineHeight: 1.5,
                                        }}>
                                        Fees are calculated on the gross amount locked from your campaign wallet. Prize
                                        money in escrow matches the prize pool total above.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {needsWalletTopUp && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => navigate(ROUTES.BRAND_WALLET)}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 999,
                                        borderColor: midnight.secondary,
                                        color: midnight.secondary,
                                        fontWeight: 700,
                                    }}>
                                    Go to wallet & top up
                                </Button>
                            )}

                            {missingAwards && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => void loadRequirements()}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 999,
                                        borderColor: midnight.primary,
                                        color: midnight.primary,
                                        fontWeight: 700,
                                    }}>
                                    Retry loading prize pool
                                </Button>
                            )}

                            {needsContestFunding && !needsWalletTopUp && !missingAwards && (
                                <Button
                                    fullWidth
                                    disabled={funding}
                                    onClick={() => void handleFundFromWallet()}
                                    startIcon={
                                        funding ? <CircularProgress size={18} color="inherit" /> : <LockOutlinedIcon />
                                    }
                                    sx={{
                                        py: 1.75,
                                        borderRadius: 999,
                                        fontWeight: 800,
                                        fontSize: 16,
                                        color: '#392700',
                                        background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                                        '&:hover': {
                                            background: `linear-gradient(90deg, #feb700, ${midnight.primary})`,
                                        },
                                    }}>
                                    {funding ? 'Locking funds…' : `Lock ${formatUsdFromCents(lockCents)} from wallet`}
                                </Button>
                            )}

                            {!needsContestFunding && !isFullyFunded && requirements?.required_total_cents === 0 && (
                                <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                                    No cash prizes configured — you can publish without locking funds.
                                </Typography>
                            )}
                        </Box>

                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontSize: 10,
                                color: midnight.onSurfaceVariant,
                                mt: 3,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                fontWeight: 700,
                            }}>
                            Funds move from your campaign wallet into contest escrow
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <BrandCampaignSubscriptionGuard
                open={subGuardOpen}
                flatFeeCents={perCampaignFeeCents}
                context="fund"
                onSubscribe={() => setTierPickerOpen(true)}
                onContinueWithFee={() => {
                    setSubGuardOpen(false);
                    // bypass plan check and lock directly
                    void (async () => {
                        if (!requirements || getFundingLockCents(requirements) <= 0) return;
                        if (requirements.wallet_shortfall_cents > 0) return;
                        setFunding(true);
                        setError(null);
                        try {
                            const result = (await dispatchPromise(fundContestFromWallet({ contestId }))) as {
                                requirements?: IContestFundingRequirements;
                            };
                            if (result?.requirements) {
                                setRequirements(result.requirements);
                            } else {
                                await loadRequirements();
                            }
                            dispatch(getWalletDashboard());
                        } catch (err) {
                            const ins = parseInsufficientWallet(err);
                            if (ins) {
                                setError(
                                    `Your campaign wallet needs ${formatUsdFromCents(
                                        ins.shortfall_cents,
                                    )} more before you can lock these funds.`,
                                );
                                dispatch(getWalletDashboard());
                                await loadRequirements();
                            }
                        } finally {
                            setFunding(false);
                        }
                    })();
                }}
                onClose={() => setSubGuardOpen(false)}
            />
            <CampaignHostTierPickerModal
                open={tierPickerOpen}
                onClose={() => setTierPickerOpen(false)}
                onSelectMonthly={handleSelectMonthly}
                onSelectLtd={handleSelectLtd}
            />
            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />
        </Box>
    );
}
